import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Modal from "../../components/common/Modal";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import { calculatePercentage, formatCurrency } from "../../utils/helpers";
import { getImageUrl } from "../../utils/imageHelper";

// Helper function to get rank suffix
const getRankSuffix = (rank) => {
  if (rank === 1) return "st";
  if (rank === 2) return "nd";
  if (rank === 3) return "rd";
  return "th";
};

// Helper function to get rank color
const getRankColor = (rank) => {
  if (rank === 1) return "from-yellow-400 to-yellow-600";
  if (rank === 2) return "from-gray-300 to-gray-500";
  if (rank === 3) return "from-orange-400 to-orange-600";
  return "from-blue-400 to-blue-600";
};

// ✅ Image Lightbox — rendered via Portal directly on document.body
const ImageLightbox = ({ isOpen, onClose, contestant }) => {
  const [imageError, setImageError] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen || !contestant) return null;

  // ✅ Portal — mounts directly on body, completely above all other modals
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Image container — stop propagation so clicking image doesn't close */}
      <div
        className="relative max-w-lg w-full max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {contestant.photo && !imageError ? (
          <img
            src={getImageUrl(contestant.photo)}
            alt={contestant.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full aspect-[3/4] flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700">
            <span className="text-white font-bold text-7xl opacity-80">
              {contestant.name?.charAt(0) || "C"}
            </span>
          </div>
        )}

        {/* Name + code overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-5">
          <h3 className="text-white font-bold text-xl drop-shadow-lg">{contestant.name}</h3>
          {contestant.contestantCode && (
            <p className="text-white/80 text-sm font-semibold mt-1">
              Code: #{contestant.contestantCode}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Contestant Card — same as original, just moved here
const ContestantCard = ({ contestant, categoryId, rank, totalVotes, onVoteClick }) => {
  const percentage = calculatePercentage(contestant.voteCount || 0, totalVotes);
  const [imageError, setImageError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      {/* ✅ Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        contestant={contestant}
      />

      <Card className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 hover:border-red-300 relative">
        {/* Rank Badge */}
        <div
          className={`absolute top-3 left-3 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${getRankColor(rank)} flex items-center justify-center shadow-lg ring-2 sm:ring-4 ring-white`}
        >
          <span className="text-white font-bold text-xs sm:text-sm">
            {rank}
            <sup className="text-[8px] sm:text-[10px]">{getRankSuffix(rank)}</sup>
          </span>
        </div>

        {/* Contestant Code Badge */}
        {contestant.contestantCode && (
          <div className="absolute top-3 right-3 z-10 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg ring-2 ring-white/50">
            <span className="text-white font-bold text-xs sm:text-sm tracking-wider">
              #{contestant.contestantCode}
            </span>
          </div>
        )}

        {/* Image — clicking opens lightbox */}
        <div
          className="relative w-full aspect-[3/4] bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
        >
          {contestant.photo && !imageError ? (
            <img
              src={getImageUrl(contestant.photo)}
              alt={contestant.name}
              className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700">
              <span className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl opacity-80">
                {contestant.name?.charAt(0) || "C"}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

          {/* Zoom hint on hover */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="bg-black/50 rounded-full p-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
            <h3 className="font-bold text-base sm:text-lg text-white drop-shadow-lg line-clamp-2">
              {contestant.name}
            </h3>
            {contestant.contestantCode && (
              <p className="text-xs sm:text-sm text-white/90 font-semibold mt-1">
                Code: #{contestant.contestantCode}
              </p>
            )}
          </div>
        </div>

      <div className="p-4 sm:p-5">
        <div className="mb-3 sm:mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 font-medium">Total Votes</span>
            <span className="text-lg sm:text-xl font-bold text-red-600">
              {contestant.voteCount || 0}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
            <div
              className="bg-gradient-to-r from-red-500 to-red-600 h-full rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1.5 font-medium">{percentage}% of total votes</p>
          {contestant.contestantCode && (
            <p className="text-xs sm:text-sm text-gray-700 font-semibold mt-2">
              Nominee Code: <span className="text-red-600">#{contestant.contestantCode}</span>
            </p>
          )}
        </div>

        <Button
          onClick={() => onVoteClick(contestant, categoryId)}
          variant="primary"
          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-sm sm:text-base py-2.5 sm:py-3 shadow-lg hover:shadow-xl transition-all"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
          Vote Now
        </Button>
      </div>
    </Card>
    </>
  );
};

// ✅ Main Category Contestants Modal
const CategoryContestantsModal = ({ isOpen, onClose, category, totalVotes, onVoteClick }) => {
  if (!category) return null;

  const sortedContestants = [...category.contestants].sort(
    (a, b) => (b.voteCount || 0) - (a.voteCount || 0)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category.name}
      size="xl"
    >
      <p className="text-sm text-gray-500 mb-6">
        {sortedContestants.length} contestant{sortedContestants.length !== 1 ? "s" : ""} — click <strong>Vote Now</strong> to cast your vote
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {sortedContestants.map((contestant, index) => (
          <ContestantCard
            key={contestant.id}
            contestant={contestant}
            categoryId={category.id}
            rank={index + 1}
            totalVotes={totalVotes}
            onVoteClick={(contestant, categoryId) => {
              onVoteClick(contestant, categoryId);
            }}
          />
        ))}
      </div>
    </Modal>
  );
};

export default CategoryContestantsModal;